import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import TeacherAddQuestion from './TeacherAddQuestion';
import TeacherEditQuestion from './TeacherEditQuestion';
import { useParams, useNavigate } from 'react-router-dom';
import { signGet } from '../../lib/s3Upload';
import { FiDownload } from 'react-icons/fi';
import './TeacherEditQuestion.css';

function inferFromPath(urlOrKey) {
    if (!urlOrKey) return '';
    const path = urlOrKey.split('?')[0] || urlOrKey;
    const ext = path.split('.').pop()?.toLowerCase();
    if (!ext) return '';
    if (['jpg','jpeg','png','gif','webp'].includes(ext)) return 'image';
    if (['mp4','webm'].includes(ext)) return 'video';
    if (ext === 'pdf') return 'application/pdf';
    if (['doc','docx'].includes(ext)) return 'application/msword';
    return '';
}

function MediaThumb({ s3Key, legacyUrl, contentType, alt = 'media', size = 200 }) {
    const [signedUrl, setSignedUrl] = useState(null);

    const kind = useMemo(() => {
        if (contentType) {
            if (contentType.startsWith('image/')) return 'image';
            if (contentType.startsWith('video/')) return 'video';
            if (contentType.startsWith('audio/')) return 'audio';
            if (contentType === 'application/pdf') return 'pdf';
            if (contentType.includes('msword') || contentType.includes('officedocument')) return 'doc';
        }
        // fallback z rozszerzenia klucza/URLa
        const guessed = inferFromPath(s3Key || legacyUrl);
        if (guessed === 'image' || guessed === 'video' || guessed === 'audio') return guessed;
        if (guessed === 'application/pdf') return 'pdf';
        if (guessed === 'application/msword') return 'doc';
        return 'unknown';
    }, [contentType, s3Key, legacyUrl]);

    useEffect(() => {
        let mounted = true;
        async function run() {
            try {
                if (legacyUrl && /^https?:\/\//i.test(legacyUrl)) {
                    if (mounted) setSignedUrl(legacyUrl);
                    return;
                }
                if (s3Key) {
                    const { url } = await signGet(s3Key);
                    if (mounted) setSignedUrl(url);
                } else if (legacyUrl) {
                    // legacy ścieżka względna – spróbuj ją przekształcić do absolutnej
                    const abs = legacyUrl.startsWith('http')
                        ? legacyUrl
                        : `${window.location.origin}${legacyUrl.startsWith('/') ? '' : '/'}${legacyUrl}`;
                    if (mounted) setSignedUrl(abs);
                }
            } catch {
                // pomiń błąd podglądu
            }
        }
        run();
        return () => { mounted = false; };
    }, [s3Key, legacyUrl]);

    if (!signedUrl) return null;

    if (kind === 'image') {
        return <a href={signedUrl} target="_blank" rel="noopener noreferrer">
            <img src={signedUrl} alt={alt} style={{ maxWidth: `${size}px`, maxHeight: `${size}px` }} />
        </a>;
    }
    if (kind === 'video') {
        return <video controls style={{ maxWidth: `${Math.round(size*1.5)}px` }}>
            <source src={signedUrl} />
            Twoja przeglądarka nie obsługuje wideo.
        </video>;
    }
    if (kind === 'audio') {
        return <audio controls src={signedUrl} />;
    }
    if (kind === 'pdf') {
        return <a href={signedUrl} target="_blank" rel="noopener noreferrer">📄 Podgląd PDF</a>;
    }
    if (kind === 'doc') {
        return <a href={signedUrl} target="_blank" rel="noopener noreferrer">📄 Podgląd DOC</a>;
    }
    return <a href={signedUrl} target="_blank" rel="noopener noreferrer">📎 Plik</a>;
}

function ExamBuilder() {
    const { id: examId } = useParams();
    const [exam, setExam] = useState(null);
    const [sections, setSections] = useState([]);
    const [newSectionTitle, setNewSectionTitle] = useState('');
    const [sectionSettings, setSectionSettings] = useState({});
    const [questions, setQuestions] = useState({});
    const [expandedQuestion, setExpandedQuestion] = useState(null);
    const [editingQuestionId, setEditingQuestionId] = useState(null);

    // --- nowości: edycja nazwy egzaminu ---
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [newExamTitle, setNewExamTitle] = useState('');
    const [savingTitle, setSavingTitle] = useState(false);
    const [titleError, setTitleError] = useState('');

    // --- NOWOŚĆ: import pytań
    const fileInputRef = useRef(null);
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState('');
    const [importOk, setImportOk] = useState('');
    const [importProgress, setImportProgress] = useState(0);
    const [importPhase, setImportPhase] = useState('');

    const navigate = useNavigate();

    const fetchSections = useCallback(async () => {
        const res = await fetch(`/api/prowadzacy/sekcje?exam_id=${examId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
        });
        const data = await res.json();
        setSections(data);
    }, [examId]);

    useEffect(() => {
        fetch(`/api/prowadzacy/egzaminy/${examId}`)
            .then(res => res.json())
            .then(data => {
                setExam(data);
                setNewExamTitle(data?.title || '');
            });

        fetchSections();
    }, [examId, fetchSections]);

    useEffect(() => {
        if (sections.length === 0) return;
        sections.forEach(section => {
            fetchQuestions(section.section_id);
        });
    }, [sections]);

    const handleAddSection = async () => {
        const res = await fetch('/api/prowadzacy/sekcje', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
            },
            body: JSON.stringify({
                exam_id: examId,
                title: newSectionTitle,
                randomize_questions: false,
                questions_per_exam: null
            })
        });

        const data = await res.json();
        if (res.ok) {
            setSections([...sections, data]);
            setNewSectionTitle('');
        }
    };

    const handleSectionSettingChange = (id, key, value) => {
        setSectionSettings(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                [key]: value
            }
        }));
    };

    const getQuestionsPerExamValue = (section) => {
        const value = sectionSettings[section.section_id]?.questions_per_exam;
        return value !== undefined ? value : section.questions_per_exam;
    };

    const shouldShowAllQuestions = (section) => {
        const value = getQuestionsPerExamValue(section);
        return value === null || value === undefined || value === '' || Number(value) <= 0;
    };

    const handleShowAllQuestionsChange = (section, checked) => {
        setSectionSettings(prev => {
            const current = prev[section.section_id] || {};
            return {
                ...prev,
                [section.section_id]: {
                    ...current,
                    randomize_questions: current.randomize_questions ?? section.randomize_questions ?? false,
                    questions_per_exam: checked ? null : (current.questions_per_exam ?? section.questions_per_exam ?? '')
                }
            };
        });
    };

    const handleSaveSectionSettings = async (sectionId) => {
        const section = sections.find(s => s.section_id === sectionId);
        if (!section) return;

        const settings = sectionSettings[sectionId] || {};
        const rawQuestionsPerExam = settings.questions_per_exam !== undefined
            ? settings.questions_per_exam
            : section.questions_per_exam;
        const questionsPerExam = rawQuestionsPerExam === null || rawQuestionsPerExam === '' || Number(rawQuestionsPerExam) <= 0
            ? null
            : Number(rawQuestionsPerExam);

        const payload = {
            randomize_questions: settings.randomize_questions ?? section.randomize_questions ?? false,
            questions_per_exam: questionsPerExam
        };

        await fetch(`/api/prowadzacy/sekcje/${sectionId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
            },
            body: JSON.stringify(payload)
        });

        setSections(prev => prev.map(s => s.section_id === sectionId ? { ...s, ...payload } : s));
        setSectionSettings(prev => {
            const next = { ...prev };
            delete next[sectionId];
            return next;
        });
    };

    const fetchQuestions = async (sectionId) => {
        const res = await fetch(`/api/prowadzacy/pytania?section_id=${sectionId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
        });
        const data = await res.json();
        setQuestions(prev => ({ ...prev, [sectionId]: data }));
    };

    const handleSaveEdits = async () => {
        for (const section of sections) {
            await fetchQuestions(section.section_id);
        }
        setEditingQuestionId(null);
    };

    // --- nowości: logika edycji nazwy egzaminu ---
    const startEditTitle = () => {
        setTitleError('');
        setNewExamTitle(exam?.title || '');
        setIsEditingTitle(true);
    };

    const cancelEditTitle = () => {
        setIsEditingTitle(false);
        setTitleError('');
    };

    const saveNewTitle = async () => {
        const trimmed = (newExamTitle || '').trim();
        if (!trimmed) {
            setTitleError('Nazwa nie może być pusta.');
            return;
        }
        setSavingTitle(true);
        setTitleError('');
        try {
            const res = await fetch(`/api/prowadzacy/egzamin/${examId}/nazwa`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
                },
                body: JSON.stringify({ title: trimmed })
            });
            if (!res.ok) {
                // spróbuj odczytać błąd z backendu
                const maybeJson = await res.json().catch(() => null);
                const msg = maybeJson?.message || `Błąd zapisu (HTTP ${res.status})`;
                throw new Error(msg);
            }
            // opcjonalnie backend może zwrócić zaktualizowany egzamin
            const data = await res.json().catch(() => null);
            const updatedTitle = data?.title || trimmed;
            setExam(prev => ({ ...(prev || {}), title: updatedTitle }));
            setIsEditingTitle(false);
        } catch (e) {
            setTitleError(e.message || 'Nie udało się zapisać nowej nazwy.');
        } finally {
            setSavingTitle(false);
        }
    };

    // --- usuń pytanie ---
    const handleDeleteQuestion = async (questionId, sectionId) => {
        const ok = window.confirm('Na pewno usunąć to pytanie? Tej operacji nie można cofnąć.');
        if (!ok) return;
        const res = await fetch(`/api/prowadzacy/pytania/${questionId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
            }
        });
        if (res.ok) {
            // odśwież listę pytań tej sekcji i zwiń
            await fetchQuestions(sectionId);
            setExpandedQuestion(null);
            if (editingQuestionId === questionId) setEditingQuestionId(null);
        } else {
            const msg = (await res.json().catch(() => null))?.message || `Nie udało się usunąć (HTTP ${res.status})`;
            alert(msg);
        }
    };
    // --- NOWOŚĆ: obsługa importu
    const handleClickImport = () => {
        fileInputRef.current?.click();
    };

    const handleDownloadTemplate = async () => {
        setImportError('');
        setImportOk('');
        try {
            const res = await fetch('/api/prowadzacy/pytania/import-template', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` }
            });
            if (!res.ok) {
                const msg = await readApiError(res, `Nie udało się pobrać szablonu (HTTP ${res.status})`);
                throw new Error(msg);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'szablon-importu-pytan.xlsx';
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            setImportError(err?.message || 'Nie udało się pobrać szablonu.');
        }
    };
    async function readApiError(res, fallbackMsg = `Błąd (HTTP ${res.status})`) {
        try {
            const data = await res.json();
            if (typeof data === 'string') return data;
            if (Array.isArray(data?.errors) && data.errors.length) return data.errors.join(', ');
            // ⬇️ preferuj 'blad', potem inne popularne klucze
            return data?.blad || data?.message || data?.error || fallbackMsg;
        } catch {
            try {
                const t = await res.text();
                return t || fallbackMsg;
            } catch {
                return fallbackMsg;
            }
        }
    }

    const uploadImportFile = (form) => new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/prowadzacy/pytania/import');
        xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token') || ''}`);

        xhr.upload.onprogress = (event) => {
            if (!event.lengthComputable) return;
            const percent = Math.round((event.loaded / event.total) * 100);
            setImportProgress(Math.min(percent, 100));
            setImportPhase(percent >= 100 ? 'Przetwarzanie pliku...' : 'Wysyłanie pliku...');
        };

        xhr.onload = () => {
            const payload = (() => {
                try { return xhr.responseText ? JSON.parse(xhr.responseText) : null; }
                catch { return null; }
            })();

            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(payload);
                return;
            }

            reject(new Error(payload?.blad || payload?.message || payload?.error || `Import nieudany (HTTP ${xhr.status})`));
        };

        xhr.onerror = () => reject(new Error('Nie udało się wysłać pliku importu.'));
        xhr.onabort = () => reject(new Error('Import został przerwany.'));
        xhr.send(form);
    });

    const handleImportFile = async (e) => {
        const file = e.target.files?.[0];
        // pozwól na ponowny wybór tego samego pliku później
        e.target.value = '';

        if (!file) return;
        setImportError('');
        setImportOk('');
        setImporting(true);
        setImportProgress(0);
        setImportPhase('Przygotowanie importu...');

        try {
            const form = new FormData();
            form.append('file', file);
            // jeżeli backend wymaga powiązania z egzaminem:
            form.append('exam_id', String(examId));
            setImportProgress(30);
            setImportPhase('Wysyłanie pliku...');

            const payload = await uploadImportFile(form);
            const importedCount =
                payload?.imported ?? payload?.count ?? payload?.created ?? null;

            setImportOk(importedCount != null
                ? `Zaimportowano ${importedCount} pyt.`
                : 'Import zakończony powodzeniem.');

            // odśwież strukturę egzaminu po imporcie
            await fetchSections();
            if (payload?.section_id) await fetchQuestions(payload.section_id);
            setImportProgress(100);
            setImportPhase('Import zakończony.');
        } catch (err) {
            setImportError(err?.message || 'Wystąpił błąd podczas importu.');
        } finally {
            setImporting(false);
        }
    };


    return (
        <div style={{ padding: '0 16px', marginTop: 80 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: 30 }}>
                <h2 style={{ margin: 0 }}>Kreator egzaminu: {exam?.title || '...'}</h2>
                {!isEditingTitle && (
                    <button onClick={startEditTitle}  className={'but-smal'} title="Edytuj nazwę egzaminu"> Edytuj nazwę</button>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    onChange={handleImportFile}
                    style={{ display: 'none' }}
                />
                <button onClick={handleClickImport}  className={'but-smal'} disabled={importing} title="Importuj pytania z pliku">
                    {importing ? 'Importowanie…' : ' Importuj pytania'}
                </button>
                <button
                    onClick={handleDownloadTemplate}
                    className={'but-smal'}
                    disabled={importing}
                    title="Pobierz szablon Excel"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                    <FiDownload aria-hidden="true" />
                    Szablon
                </button>
            </div>

            {(importing || importPhase) && (
                <div style={{ maxWidth: 520, marginTop: '-1rem', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#475569', marginBottom: 6 }}>
                        <span>{importPhase || 'Import pytań'}</span>
                        <span>{importProgress}%</span>
                    </div>
                    <div style={{ height: 8, borderRadius: 999, background: '#e5e7eb', overflow: 'hidden' }}>
                        <div
                            style={{
                                width: `${importProgress}%`,
                                height: '100%',
                                borderRadius: 999,
                                background: '#2563eb',
                                transition: 'width 0.2s ease'
                            }}
                        />
                    </div>
                </div>
            )}


            {(importError || importOk) && (
                <div style={{ marginTop: '0.5rem' }}>
                    {importError && <div style={{ color: 'crimson' }}>{importError}</div>}
                    {importOk && <div style={{ color: 'green' }}>{importOk}</div>}
                </div>
            )}




            {isEditingTitle && (
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: 30 }}>
                    <input
                        type="text"
                        value={newExamTitle}
                        onChange={e => setNewExamTitle(e.target.value)}
                        placeholder="Nowa nazwa egzaminu"
                        style={{ minWidth: 280 }}
                    />
                    <button onClick={saveNewTitle}  className={'but-smal'} disabled={savingTitle}>
                        {savingTitle ? 'Zapisywanie...' : 'Zmień nazwę'}
                    </button>
                    <button onClick={cancelEditTitle}  className={'but-smal'} disabled={savingTitle}>Anuluj</button>
                    {titleError && <span style={{ color: 'crimson' }}>{titleError}</span>}
                </div>
            )}




            {sections.map(section => (
                <div key={section.section_id} style={{ border: '1px solid #aaa', marginBottom: '2rem', padding: '1rem', background:'white'}}>
                    <h3>{section.title}</h3>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            Wyświetlaj wszystkie pytania z sekcji
                            <input
                                type="checkbox"
                                checked={shouldShowAllQuestions(section)}
                                onChange={e => handleShowAllQuestionsChange(section, e.target.checked)}
                            />
                        </label>
                        <div style={{ marginTop: 8, marginBottom: 10, color: '#64748b', fontSize: 14, lineHeight: 1.45 }}>
                            Jeżeli chcesz wyświetlać określoną liczbę pytań z sekcji, odznacz checkbox powyżej i podaj liczbę pytań dla tej sekcji. Na końcu zapisz ustawienia.
                        </div>
                        <label>
                            Liczba pytań do wyświetlenia:
                            <input
                                type="number"
                                min="1"
                                disabled={shouldShowAllQuestions(section)}
                                value={(sectionSettings[section.section_id]?.questions_per_exam ?? section.questions_per_exam) || ''}
                                onChange={e => handleSectionSettingChange(section.section_id, 'questions_per_exam', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                                style={{ marginLeft: 8 }}
                            />
                        </label><br />
                        <button  className={'but-smal'} style={{boxShadow:'none'}}  onClick={() => handleSaveSectionSettings(section.section_id)}>Zapisz ustawienia</button>
                    </div>

                    {questions[section.section_id]?.map(p => (
                        <div key={p.question_id} style={{ border: '1px solid #ccc', padding: '0.5rem', marginBottom: '0.5rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                                <strong>{p.content}</strong>
                                <button  className={'but-rozwin'} onClick={() => setExpandedQuestion(expandedQuestion === p.question_id ? null : p.question_id)}>
                                    {expandedQuestion === p.question_id ? '▲' : '▼'}
                                </button>
                            </div>

                            {expandedQuestion === p.question_id && (
                                <div style={{ marginTop: '0.5rem', paddingLeft: '1rem' }}>
                                    {editingQuestionId === p.question_id ? (
                                        <div>
                                            <TeacherEditQuestion
                                                sectionId={section.section_id}
                                                questionId={p.question_id}
                                                initialData={p}
                                                onSave={handleSaveEdits}
                                                onCancel={() => setEditingQuestionId(null)}
                                            />
                                        </div>
                                    ) : (
                                        <div>
                                            {p.files?.length > 0 && (
                                                <div>
                                                    <label>Pliki pytania:</label>
                                                    <br />
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                        {p.files.map((file, i) => (
                                                            <div style={{ padding: '10px' }} key={`existing-${i}`}>
                                                                <MediaThumb
                                                                    s3Key={file.s3_key}
                                                                    legacyUrl={file.media_url}
                                                                    contentType={file.content_type}
                                                                    alt={`plik-${file.file_id}`}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <p ><strong>Odpowiedzi:</strong></p>
                                            <ul>
                                                {p.options?.map((ans, i) => (
                                                    <li key={i}>
                                                        {ans.content} {ans.is_correct ? '(✔️ poprawna)' : ''}
                                                        {(ans.media_s3_key || ans.media_url) && (
                                                            <div style={{ marginTop: '0.3rem' }}>
                                                                <MediaThumb
                                                                    s3Key={ans.media_s3_key}
                                                                    legacyUrl={ans.media_url}
                                                                    contentType={ans.media_content_type}
                                                                    alt={`opcja-${ans.option_id}`}
                                                                    size={180}
                                                                />
                                                            </div>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>

                                            <p><strong>Typ:</strong> {p.type}</p>
                                            <p><strong>Punkty za poprawną odp:</strong> {p.points}</p>
                                            <p><strong>Punkty za brak odp:</strong> {p.no_answer_points ?? '—'}</p>
                                            <p><strong>Punkty ujemne:</strong> {p.negative_points ?? '—'}</p>
                                            <p><strong>Tagi:</strong> {p.tags?.join(', ')}</p>

                                            {/* Kontrolki edycji/usuń z warunkiem editable */}
                                            {p.editable ? (
                                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                    <button className={'but-smal'} onClick={() => setEditingQuestionId(p.question_id)}> Edytuj</button>
                                                    <button className={'but-smal'} onClick={() => handleDeleteQuestion(p.question_id, section.section_id)} style={{ color: 'white', background: '#c0392b' }}> Usuń</button>
                                                </div>
                                            ) : (
                                                <div style={{ color: '#888' }}>
                                                    To pytanie zostało już użyte w terminie egzaminu – nie można go zmienić.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}

                    <TeacherAddQuestion
                        sectionId={section.section_id}
                        onQuestionAdded={() => fetchQuestions(section.section_id)}
                    />
                </div>
            ))}

            <div style={{ marginBottom: '2rem', display:'flex' }}>
                <input
                    type="text"
                    placeholder="Nazwa nowej sekcji"
                    value={newSectionTitle}
                    onChange={e => setNewSectionTitle(e.target.value)}
                    style={{
                        flex: '0 1 50%',        // połowa szerokości widoku; chcesz połowę kontenera? użyj '50%'
                        minWidth: 280,
                        padding: '0.5rem 0.6rem',
                        marginRight: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: 8,
                    }}
                />
                <button className={'but-smal'}  onClick={handleAddSection}>Dodaj nową sekcję</button>
            </div>

            <div style={{ textAlign: 'center', marginTop: '3rem', marginBottom: 50 }}>
                <button
                    className={'but-smal'}
                    onClick={() => navigate(`/prowadzacy/egzamin/${examId}/terminy`)}
                    style={{ padding: '1rem 2rem', fontSize: '1rem' }}
                >
                    Przejdź do ustalania terminu
                </button>
            </div>
        </div>
    );
}

export default ExamBuilder;
